import React, { useState, useEffect } from 'react';
import { MatchFeedCard } from './MatchFeedCard';
import { CreateMatchModal } from './CreateMatchModal';
import { CreateFriendlyMatchModal } from './CreateFriendlyMatchModal';
import { PlayerProfileModal } from './PlayerProfileModal';
import { X, AlertCircle, Plus, ChevronDown, Edit, Trash2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { EditMatchPlayersModal } from './EditMatchPlayersModal';
import './MatchFeed.css';

interface MatchFeedTabProps {
  competitionId: string;
  currentUser: any;
  competition?: any;
  filterPlayerId?: string;
  filterRound?: number;
  activeCategory?: string | null;
  readOnly?: boolean;
}

export const MatchFeedTab: React.FC<MatchFeedTabProps> = ({ competitionId, currentUser, competition, filterPlayerId, filterRound, activeCategory, readOnly = false }) => {
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const isManager = !readOnly && (currentUser?.role === 'ADMIN' || competition?.managerId === currentUser?.id);
  
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [amistosoFormat, setAmistosoFormat] = useState('BEST_OF_3_SUPER_TIEBREAK');
  const [setsScore, setSetsScore] = useState<{p1: string, p2: string, t1?: string, t2?: string}[]>([]);
  const [winnerId, setWinnerId] = useState('');
  const [isWalkover, setIsWalkover] = useState(false);

  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduleLocation, setScheduleLocation] = useState('');
  const [scheduleCourt, setScheduleCourt] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [showEditPlayersModal, setShowEditPlayersModal] = useState(false);
  const [showNewRoundModal, setShowNewRoundModal] = useState(false);
  const [newRoundMode, setNewRoundMode] = useState('MANUAL');
  const [newRoundDate, setNewRoundDate] = useState('');
  const [showDropdownForMatch, setShowDropdownForMatch] = useState<string | null>(null);
  const [showCreateMatchModal, setShowCreateMatchModal] = useState(false);
  const [showCreateFriendlyMatchModal, setShowCreateFriendlyMatchModal] = useState(false);
  
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'AWAITING_APPROVAL' | 'COMPLETED'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  useEffect(() => {
    fetchMatches();
  }, [competitionId]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      let endpoint = competitionId ? `/api/competitions/${competitionId}/matches` : `/api/feed`;
      if (!competitionId && filterPlayerId) {
        endpoint += `?playerId=${filterPlayerId}`;
      }
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatches(data);
      }
    } catch (e) {
      console.log('Error fetching matches', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMatch = async (matchId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta partida?')) return;
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Partida excluída com sucesso!');
        fetchMatches();
        setShowDropdownForMatch(null);
      } else {
        toast.error('Erro ao excluir partida');
      }
    } catch (err) {
      toast.error('Erro de conexão');
    }
  };

  const handleStartRound = async () => {
    const isTournament = competition?.type === 'TOURNAMENT';
    const isAutoDraw = isTournament 
      ? competition?.config?.tournamentRules?.matchGenerationMode === 'AUTO_DRAW'
      : competition?.config?.ladderRules?.matchGenerationMode === 'AUTO_DRAW';

    try {
      setLoading(true);
      if (isAutoDraw) {
        const payload: any = {};
        if (!isTournament) {
          payload.roundEndMode = newRoundMode;
          if (newRoundMode === 'AUTO') {
            payload.roundEndDate = newRoundDate;
          }
        }

        const res = await fetch(`${API_URL}/api/competitions/${competitionId}/matches/auto-draw`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          toast.success(isTournament ? 'Chave gerada com sucesso!' : 'Rodada gerada com sucesso!');
          setShowNewRoundModal(false);
          setTimeout(() => window.location.reload(), 1500);
        } else {
          const data = await res.json();
          toast.error(data.error || 'Erro ao gerar partidas');
        }
      } else {
        // Manual/Challenge round start
        const updatedConfig = { ...competition?.config };
        updatedConfig.currentRoundEndMode = newRoundMode;
        if (newRoundMode === 'AUTO') {
          updatedConfig.currentRoundEndDate = newRoundDate;
        } else {
          delete updatedConfig.currentRoundEndDate;
        }
        
        const res = await fetch(`${API_URL}/api/competitions/${competitionId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ config: updatedConfig })
        });
        if (res.ok) {
          toast.success('Rodada iniciada com sucesso (Modo Manual/Desafio)');
          setShowNewRoundModal(false);
          setTimeout(() => window.location.reload(), 1500);
        } else {
          const data = await res.json();
          toast.error(data.error || 'Erro ao iniciar rodada');
        }
      }
    } catch (err) {
      toast.error('Erro de conexão ao iniciar rodada');
    } finally {
      setLoading(false);
    }
  };

  const handleReportScore = (match: any) => {
    setSelectedMatch(match);
    
    // Initialize empty sets based on format
    const isAmistoso = !competition && !match.competitionId;
    const format = isAmistoso ? 'BEST_OF_3_SUPER_TIEBREAK' : (competition?.config?.matchEngine?.scoreFormat || 'BEST_OF_3_SUPER_TIEBREAK');
    if (isAmistoso) setAmistosoFormat('BEST_OF_3_SUPER_TIEBREAK');

    const isSingleSet = ['PRO_SET_8', 'PRO_SET_9', 'SINGLE_SET_6', 'FAST_4'].includes(format);
    const isBestOf5 = format === 'BEST_OF_5';
    let initialSets = [{p1: '', p2: '', t1: '', t2: ''}];
    if (!isSingleSet) {
      initialSets = isBestOf5 
        ? [{p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}]
        : [{p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}];
    }
    
    // If it's an edit of an existing match, we could parse the score, but for now we initialize empty
    setSetsScore(initialSets);
    setWinnerId('');
    setIsWalkover(false);
    setReportModalVisible(true);
  };

  const submitReport = async () => {
    let finalScore = '';
    
    if (isWalkover) {
      if (!winnerId) {
        toast.error('Selecione o vencedor');
        return;
      }
      const walkoverRule = competition?.config?.matchEngine?.walkoverRule || 'SCORE_6_0_6_0';
      finalScore = walkoverRule === 'NO_GAMES_SCORED' ? '1/0 (W.O.)' : '6/0 6/0 (W.O.)';
    } else {
      // Validate scores
      const format = competition?.config?.matchEngine?.scoreFormat || 'BEST_OF_3_SUPER_TIEBREAK';
      let isValidScore = true;
    
    for (let i = 0; i < setsScore.length; i++) {
      const s = setsScore[i];
      const p1Str = s.p1.trim();
      const p2Str = s.p2.trim();
      if (p1Str === '' && p2Str === '') continue;
      
      const p1 = parseInt(p1Str || '0');
      const p2 = parseInt(p2Str || '0');
      if (isNaN(p1) || isNaN(p2) || p1 < 0 || p2 < 0) {
        isValidScore = false;
        break;
      }
      
      const max = Math.max(p1, p2);
      const min = Math.min(p1, p2);
      const isSuperTiebreak = (i === 2 && format === 'BEST_OF_3_SUPER_TIEBREAK');

      let needsTiebreak = false;
      if (!isSuperTiebreak) {
        if (format === 'PRO_SET_8' && max === 9 && min === 8) needsTiebreak = true;
        if (format === 'PRO_SET_9' && max === 10 && min === 9) needsTiebreak = true;
        if (format === 'FAST_4' && max === 4 && min === 3) needsTiebreak = true;
        if (['SINGLE_SET_6', 'BEST_OF_3_REGULAR', 'BEST_OF_3_SUPER_TIEBREAK'].includes(format) && max === 7 && min === 6) needsTiebreak = true;
      }

      if (isSuperTiebreak) {
        if (!(max >= 10 && max - min >= 2 && (max === 10 || max - min === 2))) {
          isValidScore = false;
        }
      } else if (needsTiebreak) {
        const t1Str = (s.t1 || '').trim();
        const t2Str = (s.t2 || '').trim();
        const t1 = parseInt(t1Str || '0');
        const t2 = parseInt(t2Str || '0');
        if (isNaN(t1) || isNaN(t2) || t1 < 0 || t2 < 0) {
          isValidScore = false;
          break;
        }

        if (p1 > p2 && t1 < t2) isValidScore = false;
        if (p2 > p1 && t2 < t1) isValidScore = false;

        const tMax = Math.max(t1, t2);
        const tMin = Math.min(t1, t2);
        if (format === 'FAST_4') {
          if (tMax !== 5 || tMin > 4) isValidScore = false;
        } else {
          if (!(tMax >= 7 && tMax - tMin >= 2 && (tMax === 7 || tMax - tMin === 2))) {
            isValidScore = false;
          }
        }
      } else {
        switch (format) {
          case 'PRO_SET_8':
            if (!((max === 8 && min <= 6) || (max === 9 && (min === 7 || min === 8)))) isValidScore = false;
            break;
          case 'PRO_SET_9':
            if (!((max === 9 && min <= 7) || (max === 10 && (min === 8 || min === 9)))) isValidScore = false;
            break;
          case 'FAST_4':
            if (!(max === 4 && min <= 3)) isValidScore = false;
            break;
          default: // SINGLE_SET_6, BEST_OF_3_REGULAR, BEST_OF_3_SUPER_TIEBREAK
            if (!((max === 6 && min <= 4) || (max === 7 && (min === 5 || min === 6)))) isValidScore = false;
            break;
        }
      }
    }

      if (!isValidScore) {
        toast.error('Placar inválido. Siga as regras oficiais de tênis para o formato escolhido.');
        return;
      }

      // Build final score string
      const validSets = setsScore.filter(s => s.p1.trim() !== '' || s.p2.trim() !== '');
      finalScore = validSets.map((s, i) => {
        let str = `${s.p1.trim() || '0'}/${s.p2.trim() || '0'}`;
        
        const p1 = parseInt(s.p1.trim() || '0');
        const p2 = parseInt(s.p2.trim() || '0');
        const max = Math.max(p1, p2);
        const min = Math.min(p1, p2);
        const isSuperTiebreak = (i === 2 && format === 'BEST_OF_3_SUPER_TIEBREAK');
        
        let needsTiebreak = false;
        if (!isSuperTiebreak) {
          if (format === 'PRO_SET_8' && max === 9 && min === 8) needsTiebreak = true;
          if (format === 'PRO_SET_9' && max === 10 && min === 9) needsTiebreak = true;
          if (format === 'FAST_4' && max === 4 && min === 3) needsTiebreak = true;
          if (['SINGLE_SET_6', 'BEST_OF_3_REGULAR', 'BEST_OF_3_SUPER_TIEBREAK'].includes(format) && max === 7 && min === 6) needsTiebreak = true;
        }
        
        if (needsTiebreak) {
          const t1 = parseInt((s.t1 || '').trim() || '0');
          const t2 = parseInt((s.t2 || '').trim() || '0');
          const loserTbScore = p1 > p2 ? t2 : t1;
          str += `(${loserTbScore})`;
        }
        return str;
      }).join(' ');

      if (!finalScore && !winnerId) {
        toast.error('Preencha o placar ou pelo menos selecione o vencedor');
        return;
      }
      if (!winnerId) {
        toast.error('Selecione o vencedor');
        return;
      }
    }
    
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${selectedMatch.id}/report`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ score: finalScore, winnerId })
      });
      if (res.ok) {
        setReportModalVisible(false);
        fetchMatches();
        toast.success('Placar enviado com sucesso!');
      } else {
        toast.error('Falha ao lançar resultado');
      }
    } catch (e) {
      console.log(e);
      toast.error('Falha ao comunicar com servidor');
    }
  };

  const handleApprove = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchMatches();
        toast.success('Placar aprovado!');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleReject = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchMatches();
        toast.error('Placar reprovado.');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleAcceptChallenge = async (matchId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/accept-challenge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchMatches();
        toast.success('Desafio aceito!');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleRejectChallenge = async (matchId: string) => {
    if (!window.confirm('Tem certeza que deseja recusar o desafio?')) return;
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${matchId}/reject-challenge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchMatches();
        toast.error('Desafio recusado.');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const openScheduleModal = (match: any) => {
    setSelectedMatch(match);
    setScheduleLocation(match.location || '');
    setScheduleCourt(match.court || '');
    setScheduleDate(match.date ? new Date(match.date).toISOString().split('T')[0] : '');
    setScheduleTime(match.time || '');
    setScheduleModalVisible(true);
  };

  const submitSchedule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/${selectedMatch.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ location: scheduleLocation, court: scheduleCourt, date: scheduleDate, time: scheduleTime })
      });
      if (res.ok) {
        setScheduleModalVisible(false);
        fetchMatches();
        toast.success('Agendamento salvo com sucesso!');
      }
    } catch(e) {
      toast.error('Erro ao salvar agendamento');
    }
  };

  const renderApprovalBanner = (match: any) => {
    if (readOnly) return null;

    const p1Id = match.player1?.id || match.player1Id || match.team1Player1Id;
    const p2Id = match.player2?.id || match.player2Id || match.team2Player1Id;

    if (!isManager && currentUser?.id !== p1Id && currentUser?.id !== p2Id) {
      if (match.status === 'AWAITING_APPROVAL') {
        return (
          <div className="mf-banner-info">
            Aguardando aprovação de resultado
          </div>
        );
      }
      return null;
    }

    const canEdit = isManager || currentUser?.id === p1Id || currentUser?.id === p2Id;

    if (match.status === 'PENDING') {
      return (
        <div className="mf-banner-action" style={{ display: 'flex', gap: '8px' }}>
          <button className="mf-btn-primary" style={{ flex: 1 }} onClick={() => handleReportScore(match)}>
            Lançar Resultado
          </button>
          {canEdit && (
            <button className="mf-btn-secondary" style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'white', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => openScheduleModal(match)}>
              Agendar / Local
            </button>
          )}
        </div>
      );
    }
    
    if (match.status === 'COMPLETED' && isManager) {
      return (
        <div className="mf-banner-action" style={{ display: 'flex', gap: '8px' }}>
          <button className="mf-btn-secondary" style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'white', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleReportScore(match)}>
            Alterar Resultado
          </button>
          <button className="mf-btn-secondary" style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'white', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => openScheduleModal(match)}>
            Editar Local
          </button>
        </div>
      );
    }

    if (match.status === 'AWAITING_APPROVAL') {
      const winnerName = match.reportedWinnerId 
        ? (match.reportedWinnerId === (match.player1?.id || match.player1Id) 
            ? (match.player1?.name || match.player1Name) 
            : (match.player2?.name || match.player2Name))
        : '';
      const scoreStr = typeof match.reportedScore === 'string' ? match.reportedScore : (Array.isArray(match.reportedScore) ? match.reportedScore.map((s:any) => `${s.p1 || 0}/${s.p2 || 0}`).join(' ') : JSON.stringify(match.reportedScore));

      if (match.reportedById === currentUser?.id) {
        return (
          <div className="mf-banner-info">
            Placar enviado. Aguardando adversário aprovar.
            {winnerName ? <div style={{marginTop: '4px', fontWeight: 'bold'}}>Vitória de {winnerName}</div> : null}
          </div>
        );
      } else {
        return (
          <div className="mf-banner-action-approval">
            <div style={{color: '#856404', fontWeight: 'bold', fontSize: '14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px'}}>
              <AlertCircle size={16} /> Adversário lançou: {scoreStr} {winnerName ? `(Vitória de ${winnerName})` : ''}
            </div>
            <div style={{display: 'flex', gap: '12px', width: '100%', maxWidth: '300px'}}>
              <button className="mf-btn-danger" style={{flex: 1}} onClick={() => handleReject(match.id)}>
                Reprovar
              </button>
              <button className="mf-btn-primary" style={{flex: 1, backgroundColor: '#2b5c3a'}} onClick={() => handleApprove(match.id)}>
                Aprovar
              </button>
            </div>
          </div>
        );
      }
    }

    if (match.status === 'CHALLENGE_PROPOSED') {
      if (match.player1Id === currentUser?.id) {
        return (
          <div className="mf-banner-info">
            Desafio enviado. Aguardando adversário aceitar.
          </div>
        );
      } else {
        return (
          <div className="mf-banner-action-approval">
            <div style={{color: '#856404', fontWeight: 'bold', fontSize: '14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px'}}>
              <AlertCircle size={16} /> Você foi desafiado!
            </div>
            <div style={{display: 'flex', gap: '12px', width: '100%', maxWidth: '300px'}}>
              <button className="mf-btn-danger" style={{flex: 1}} onClick={() => handleRejectChallenge(match.id)}>
                Recusar
              </button>
              <button className="mf-btn-primary" style={{flex: 1, backgroundColor: '#2b5c3a'}} onClick={() => handleAcceptChallenge(match.id)}>
                Aceitar
              </button>
            </div>
          </div>
        );
      }
    }

    return null;
  };

  const validMatches = matches.filter(m => m.player1Id !== 'TBD' && m.player2Id !== 'TBD');
  const sortedAll = [...validMatches].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const indexedValid = sortedAll.map((m) => {
    let effectiveStatus = m.status;
    if (effectiveStatus === 'PENDING' && (!m.player1Id || !m.player2Id)) {
      effectiveStatus = 'WAITING';
    }
    // We now receive displayMatchNumber from the backend, so we don't need to compute it.
    // However, for friendly matches or legacy matches that might not have it, we could fallback if we wanted, 
    // but the backend query for feed always provides it now.
    return { ...m, status: effectiveStatus };
  });

  let displayedMatches = filterPlayerId 
    ? indexedValid.filter(m => m.player1?.id === filterPlayerId || m.player2?.id === filterPlayerId || m.team1Player1Id === filterPlayerId || m.team2Player1Id === filterPlayerId || m.player1Id === filterPlayerId || m.player2Id === filterPlayerId)
    : indexedValid;
    
  if (filterRound) {
    displayedMatches = displayedMatches.filter(m => m.round === filterRound);
  }

  if (activeCategory) {
    displayedMatches = displayedMatches.filter(m => m.category === activeCategory);
  }

  // Reverse back to newest first
  displayedMatches.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (statusFilter !== 'ALL') {
    displayedMatches = displayedMatches.filter(m => m.status === statusFilter);
  }

  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    displayedMatches = displayedMatches.filter(m => {
      const p1 = m.player1?.name || m.player1Name || '';
      const p2 = m.player2?.name || m.player2Name || '';
      return p1.toLowerCase().includes(q) || p2.toLowerCase().includes(q);
    });
  }

  // Count metrics for filters
  const countAll = indexedValid.length;
  const countPending = indexedValid.filter(m => m.status === 'PENDING').length;
  const countAwaiting = indexedValid.filter(m => m.status === 'AWAITING_APPROVAL').length;
  const countCompleted = indexedValid.filter(m => m.status === 'COMPLETED').length;

  return (
    <div className="mf-container">
      {(!!competitionId || !!filterPlayerId) && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <Search size={18} color="rgba(255,255,255,0.4)" style={{ position: 'absolute', left: '16px', top: '14px' }} />
            <input 
              type="text"
              placeholder="Buscar por jogador..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '15px' }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }} className="hide-scrollbar">
            <button 
              onClick={() => setStatusFilter('ALL')}
              style={{ padding: '8px 16px', borderRadius: '20px', whiteSpace: 'nowrap', border: 'none', background: statusFilter === 'ALL' ? '#3B593F' : 'transparent', color: statusFilter === 'ALL' ? '#FFF' : 'rgba(255,255,255,0.6)', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Todos ({countAll})
            </button>
            <button 
              onClick={() => setStatusFilter('PENDING')}
              style={{ padding: '8px 16px', borderRadius: '20px', whiteSpace: 'nowrap', border: 'none', background: statusFilter === 'PENDING' ? '#3B593F' : 'transparent', color: statusFilter === 'PENDING' ? '#FFF' : 'rgba(255,255,255,0.6)', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Pendentes ({countPending})
            </button>
            <button 
              onClick={() => setStatusFilter('AWAITING_APPROVAL')}
              style={{ padding: '8px 16px', borderRadius: '20px', whiteSpace: 'nowrap', border: 'none', background: statusFilter === 'AWAITING_APPROVAL' ? '#3B593F' : 'transparent', color: statusFilter === 'AWAITING_APPROVAL' ? '#FFF' : 'rgba(255,255,255,0.6)', fontWeight: 'bold', cursor: 'pointer' }}
            >
              A confirmar ({countAwaiting})
            </button>
            <button 
              onClick={() => setStatusFilter('COMPLETED')}
              style={{ padding: '8px 16px', borderRadius: '20px', whiteSpace: 'nowrap', border: 'none', background: statusFilter === 'COMPLETED' ? '#3B593F' : 'transparent', color: statusFilter === 'COMPLETED' ? '#FFF' : 'rgba(255,255,255,0.6)', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Concluídos ({countCompleted})
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)'}}>
          Carregando feed...
        </div>
      ) : (
        <>
          {displayedMatches.length === 0 ? (
            <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)'}}>
              Nenhuma partida encontrada no feed.
            </div>
          ) : (
            displayedMatches.map(match => (
            <div key={match.id} style={{position: 'relative', marginBottom: '16px'}}>
              {isManager && (
                <div style={{ position: 'absolute', right: '16px', top: '16px', zIndex: 10 }}>
                  <button 
                    onClick={() => setShowDropdownForMatch(showDropdownForMatch === match.id ? null : match.id)}
                    style={{ background: 'rgba(0,0,0,0.2)', border: 'none', color: 'white', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                  >
                    <ChevronDown size={16} />
                  </button>
                  {showDropdownForMatch === match.id && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', background: '#2D1E4B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', zIndex: 10, minWidth: '160px', marginTop: '4px' }}>
                      <button onClick={() => { setSelectedMatch(match); setShowEditPlayersModal(true); setShowDropdownForMatch(null); }} style={{ width: '100%', padding: '12px', background: 'transparent', border: 'none', color: 'white', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}><Edit size={14} /> Alterar Jogadores</button>
                      <button onClick={() => handleDeleteMatch(match.id)} style={{ width: '100%', padding: '12px', background: 'transparent', border: 'none', color: '#ff4444', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><Trash2 size={14} /> Excluir Partida</button>
                    </div>
                  )}
                </div>
              )}
              {renderApprovalBanner(match)}
              <MatchFeedCard 
                matchId={match.id}
                status={match.status === 'AWAITING_APPROVAL' ? 'PENDING' : match.status}
                player1={match.player1 || {id: match.player1Id || match.team1Player1Id, name: match.player1Name || (match.player1Id === 'TBD' ? 'A Definir' : (match.team1 || 'A Definir')), photoUrl: match.player1Photo}}
                player2={match.player2 || {id: match.player2Id || match.team2Player1Id, name: match.player2Name || (match.player2Id === 'TBD' ? 'A Definir' : (match.team2 || 'A Definir')), photoUrl: match.player2Photo}}
                date={match.date}
                time={match.time}
                location={match.location}
                court={match.court}
                score={match.score}
                winnerId={match.winnerId}
                competitionName={match.competitionName || competition?.name || 'Amistoso'}
                stage={match.stage || (match.round ? `Rodada ${match.round}` : undefined)}
                category={match.category}
                displayMatchNumber={match.displayMatchNumber}
                pointsConfig={competition?.config?.pointsConfig || (typeof match.competitionConfig === 'string' ? JSON.parse(match.competitionConfig)?.pointsConfig : match.competitionConfig?.pointsConfig)}
                onDelete={handleDeleteMatch}
                onPlayerClick={setSelectedPlayerId}
              />
            </div>
          ))
        )}
      </>
    )}

      {/* Report Modal */}
      {reportModalVisible && (
        <div className="mf-modal-overlay" onClick={() => setReportModalVisible(false)}>
          <div className="mf-modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '400px'}}>
            <div className="mf-modal-header">
              <h3 style={{margin: 0}}>Lançar Resultado</h3>
              <button className="mf-close-btn" style={{marginTop: 0}} onClick={() => setReportModalVisible(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '16px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)'}}>
                <input 
                  type="checkbox" 
                  checked={isWalkover} 
                  onChange={(e) => setIsWalkover(e.target.checked)} 
                  style={{width: '20px', height: '20px', cursor: 'pointer'}}
                />
                <span style={{color: 'white', fontWeight: 'bold'}}>Vitória por W.O. (Desistência/Não comparecimento)</span>
              </label>

              {!isWalkover && (
                <>
                  {(!competition && !selectedMatch?.competitionId) && (
                    <div style={{marginBottom: '16px'}}>
                      <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 'bold'}}>
                        Formato do Placar
                      </label>
                      <select 
                        className="mf-text-input" 
                        style={{width: '100%', padding: '12px'}}
                        value={amistosoFormat}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAmistosoFormat(val);
                          const isSingleSet = ['PRO_SET_8', 'PRO_SET_9', 'SINGLE_SET_6', 'FAST_4'].includes(val);
                          const isBestOf5 = val === 'BEST_OF_5';
                          let initialSets = [{p1: '', p2: '', t1: '', t2: ''}];
                          if (!isSingleSet) {
                            initialSets = isBestOf5 
                              ? [{p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}]
                              : [{p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}, {p1: '', p2: '', t1: '', t2: ''}];
                          }
                          setSetsScore(initialSets);
                        }}
                      >
                        <option value="SINGLE_SET_6">1 Set (Padrão)</option>
                        <option value="BEST_OF_3_REGULAR">Melhor de 3 Sets (Normal)</option>
                        <option value="BEST_OF_3_SUPER_TIEBREAK">2 Sets + Supertiebreak</option>
                        <option value="BEST_OF_5">Melhor de 5 Sets</option>
                        <option value="FAST_4">Fast 4</option>
                      </select>
                    </div>
                  )}

                  <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px', textTransform: 'uppercase', fontWeight: 'bold'}}>
                    Placar por Set
                  </label>
                  
                  <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                {setsScore.map((setObj, index) => {
                  const format = (!competition && !selectedMatch?.competitionId) ? amistosoFormat : (competition?.config?.matchEngine?.scoreFormat || 'BEST_OF_3_SUPER_TIEBREAK');
                  let setLabel = `Set ${index + 1}`;
                  if (index === 2 && format === 'BEST_OF_3_SUPER_TIEBREAK') {
                    setLabel = 'Super Tiebreak';
                  }
                  
                  let needsTb = false;
                  const isSuperTb = (index === 2 && format === 'BEST_OF_3_SUPER_TIEBREAK');
                  const p1Val = parseInt(setObj.p1);
                  const p2Val = parseInt(setObj.p2);
                  if (!isNaN(p1Val) && !isNaN(p2Val) && !isSuperTb) {
                    const max = Math.max(p1Val, p2Val);
                    const min = Math.min(p1Val, p2Val);
                    if (format === 'PRO_SET_8' && max === 9 && min === 8) needsTb = true;
                    if (format === 'PRO_SET_9' && max === 10 && min === 9) needsTb = true;
                    if (format === 'FAST_4' && max === 4 && min === 3) needsTb = true;
                    if (['SINGLE_SET_6', 'BEST_OF_3_REGULAR', 'BEST_OF_3_SUPER_TIEBREAK'].includes(format) && max === 7 && min === 6) needsTb = true;
                  }

                  return (
                    <div key={index} style={{display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <div style={{width: '90px', fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: 'bold'}}>
                          {setLabel}
                        </div>
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                          {index === 0 && <span style={{fontSize: '10px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px', maxWidth: '80px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{selectedMatch?.player1Name || selectedMatch?.player1?.name || selectedMatch?.team1 || 'Jogador 1'}</span>}
                          <input 
                            type="number" 
                            className="mf-text-input" 
                            style={{width: '60px', textAlign: 'center', padding: '10px'}}
                            value={setObj.p1} 
                            onChange={e => {
                              const newSets = [...setsScore];
                              newSets[index].p1 = e.target.value;
                              setSetsScore(newSets);
                            }}
                            placeholder="0"
                          />
                        </div>
                        <span style={{color: 'rgba(255,255,255,0.4)', fontWeight: 'bold'}}>X</span>
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                          {index === 0 && <span style={{fontSize: '10px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px', maxWidth: '80px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{selectedMatch?.player2Name || selectedMatch?.player2?.name || selectedMatch?.team2 || 'Jogador 2'}</span>}
                          <input 
                            type="number" 
                            className="mf-text-input" 
                            style={{width: '60px', textAlign: 'center', padding: '10px'}}
                            value={setObj.p2} 
                            onChange={e => {
                              const newSets = [...setsScore];
                              newSets[index].p2 = e.target.value;
                              setSetsScore(newSets);
                            }}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      
                      {needsTb && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '12px', borderLeft: '1px solid rgba(255,255,255,0.1)'}}>
                          <div style={{fontSize: '11px', color: 'var(--primary-color)', fontWeight: 'bold'}}>TB</div>
                          <input 
                            type="number" 
                            className="mf-text-input" 
                            style={{width: '45px', textAlign: 'center', padding: '8px', fontSize: '12px', background: 'rgba(0,0,0,0.3)'}}
                            value={setObj.t1 || ''} 
                            onChange={e => {
                              const newSets = [...setsScore];
                              newSets[index].t1 = e.target.value;
                              setSetsScore(newSets);
                            }}
                            placeholder="0"
                          />
                          <span style={{color: 'rgba(255,255,255,0.4)', fontSize: '12px'}}>x</span>
                          <input 
                            type="number" 
                            className="mf-text-input" 
                            style={{width: '45px', textAlign: 'center', padding: '8px', fontSize: '12px', background: 'rgba(0,0,0,0.3)'}}
                            value={setObj.t2 || ''} 
                            onChange={e => {
                              const newSets = [...setsScore];
                              newSets[index].t2 = e.target.value;
                              setSetsScore(newSets);
                            }}
                            placeholder="0"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>

            <div style={{marginBottom: '24px'}}>
              <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 'bold'}}>
                Quem Venceu?
              </label>
              <div style={{display: 'flex', gap: '8px'}}>
                <button 
                  style={{flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: winnerId === (selectedMatch?.player1?.id || selectedMatch?.team1Player1Id) ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)', color: winnerId === (selectedMatch?.player1?.id || selectedMatch?.team1Player1Id) ? 'white' : 'white', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'}}
                  onClick={() => setWinnerId(selectedMatch?.player1?.id || selectedMatch?.team1Player1Id)}
                >
                  {selectedMatch?.player1Name || selectedMatch?.player1?.name || selectedMatch?.team1 || 'Equipe 1'}
                </button>
                <button 
                  style={{flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: winnerId === (selectedMatch?.player2?.id || selectedMatch?.team2Player1Id) ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)', color: winnerId === (selectedMatch?.player2?.id || selectedMatch?.team2Player1Id) ? 'white' : 'white', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'}}
                  onClick={() => setWinnerId(selectedMatch?.player2?.id || selectedMatch?.team2Player1Id)}
                >
                  {selectedMatch?.player2Name || selectedMatch?.player2?.name || selectedMatch?.team2 || 'Equipe 2'}
                </button>
              </div>
            </div>

            <div style={{display: 'flex', gap: '12px'}}>
              <button 
                style={{flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 'bold', cursor: 'pointer'}} 
                onClick={() => setReportModalVisible(false)}
              >
                Cancelar
              </button>
              <button 
                style={{flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: 'var(--primary-color)', color: 'white', fontWeight: 'bold', cursor: 'pointer'}} 
                onClick={submitReport}
              >
                Enviar p/ Aprovação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModalVisible && (
        <div className="mf-modal-overlay" onClick={() => setScheduleModalVisible(false)}>
          <div className="mf-modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '400px'}}>
            <div className="mf-modal-header">
              <h3 style={{margin: 0}}>Agendar Partida</h3>
              <button className="mf-close-btn" style={{marginTop: 0}} onClick={() => setScheduleModalVisible(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 'bold'}}>Data</label>
              <input type="date" className="mf-text-input" style={{width: '100%', boxSizing: 'border-box'}} value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} />
            </div>
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 'bold'}}>Hora</label>
              <input type="time" className="mf-text-input" style={{width: '100%', boxSizing: 'border-box'}} value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} />
            </div>
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 'bold'}}>Clube/Local</label>
              <input type="text" className="mf-text-input" style={{width: '100%', boxSizing: 'border-box'}} value={scheduleLocation} onChange={e => setScheduleLocation(e.target.value)} placeholder="Ex: Clube Pinheiros" />
            </div>
            <div style={{marginBottom: '24px'}}>
              <label style={{display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 'bold'}}>Quadra</label>
              <input type="text" className="mf-text-input" style={{width: '100%', boxSizing: 'border-box'}} value={scheduleCourt} onChange={e => setScheduleCourt(e.target.value)} placeholder="Ex: Quadra 2" />
            </div>

            <div style={{display: 'flex', gap: '12px'}}>
              <button style={{flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 'bold', cursor: 'pointer'}} onClick={() => setScheduleModalVisible(false)}>
                Cancelar
              </button>
              <button style={{flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: 'var(--primary-color)', color: 'white', fontWeight: 'bold', cursor: 'pointer'}} onClick={submitSchedule}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAB for creating matches (Manager in competition OR anyone in global feed) */}
      {(competition && isManager) && (
        <button
          onClick={() => setShowCreateMatchModal(true)}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            borderRadius: '28px',
            background: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 100
          }}
        >
          <Plus size={24} />
        </button>
      )}
      
      {!competitionId && !readOnly && currentUser && (
        <button
          onClick={() => setShowCreateFriendlyMatchModal(true)}
          style={{
            position: 'fixed',
            bottom: '80px',
            right: '24px',
            padding: '0 24px',
            height: '56px',
            borderRadius: '28px',
            background: '#2b5c3a',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer',
            zIndex: 100,
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          <Plus size={24} /> Amistoso
        </button>
      )}

      {competition && isManager && (
        <CreateMatchModal
          isOpen={showCreateMatchModal}
          onClose={() => setShowCreateMatchModal(false)}
          competition={competition}
          category={activeCategory}
          onSuccess={fetchMatches}
        />
      )}

      {!competitionId && showCreateFriendlyMatchModal && (
        <CreateFriendlyMatchModal
          isOpen={showCreateFriendlyMatchModal}
          onClose={() => setShowCreateFriendlyMatchModal(false)}
          onSuccess={() => {
            toast.success('Amistoso criado com sucesso!');
            fetchMatches();
          }}
          currentUser={currentUser}
        />
      )}

      {showNewRoundModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <h3>Iniciar Nova Rodada</h3>
            <p style={{ fontSize: '14px', color: '#ccc', marginBottom: '16px' }}>
              Defina como a rodada será encerrada.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Modo de Encerramento</label>
              <select 
                value={newRoundMode} 
                onChange={(e) => setNewRoundMode(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', background: '#1C1231', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
              >
                <option value="MANUAL">Apenas Manualmente</option>
                <option value="AUTO">Automático (Data Programada)</option>
              </select>
            </div>

            {newRoundMode === 'AUTO' && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Data Limite</label>
                <input 
                  type="date"
                  value={newRoundDate}
                  onChange={(e) => setNewRoundDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', background: '#1C1231', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button 
                onClick={() => setShowNewRoundModal(false)}
                style={{ padding: '10px 16px', borderRadius: '6px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button 
                onClick={handleStartRound}
                style={{ padding: '10px 16px', borderRadius: '6px', background: '#3A8E58', border: 'none', color: '#FFF', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Iniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedPlayerId && (
        <PlayerProfileModal 
          playerId={selectedPlayerId} 
          onClose={() => setSelectedPlayerId(null)} 
        />
      )}

      {selectedMatch && (
        <EditMatchPlayersModal
          isOpen={showEditPlayersModal}
          onClose={() => { setShowEditPlayersModal(false); setSelectedMatch(null); }}
          match={selectedMatch}
          competition={competition}
          onSuccess={fetchMatches}
        />
      )}
    </div>
  );
};
